<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Store } from 'lucide-vue-next'
import { useAuth } from '../stores/useAuth.js'
import { coreState } from '../stores/core.js'
import { supabase } from '../supabase.js'
import { useToast } from '../composables/useToast.js'

const router = useRouter()
const route = useRoute()
const auth = useAuth()
const { showToast } = useToast()

const showConfirmation = ref(false)
const inviteToken = ref(null)

// Helper to find token
const findToken = () => {
    // 1. URL Query
    const urlParams = new URLSearchParams(window.location.search)
    let token = urlParams.get('invite_token')

    if (token) {
        sessionStorage.setItem('invite_token', token)
        // Clean URL? Maybe later.
        return token
    }

    // 2. Session/Local Storage
    token = sessionStorage.getItem('invite_token')
    if (token) return token

    token = localStorage.getItem('invite_token')
    if (token) {
        sessionStorage.setItem('invite_token', token)
        localStorage.removeItem('invite_token')
        return token
    }

    return null
}

const checkInvite = () => {
    // 1. Check Team Invite
    const token = findToken()
    const user = auth.authState.user
    const profile = auth.authState.profile

    // Wait until profile exists and signup is completed to show the global popup
    if (token && user && profile?.signupCompleted) {
        inviteToken.value = token
        showConfirmation.value = true
        return;
    }

    // 2. Partnership Token handled by GlobalPartnershipHandler.vue
    // We do nothing here to avoid conflicts.
}

// Watch for Auth or Route changes
watch(() => [auth.authState.user, auth.authState.profile, route.query], () => {
    checkInvite()
}, { deep: true })

onMounted(() => {
    checkInvite()
})

const handleConfirm = async () => {
    if (!inviteToken.value) return

    try {
        const { data, error } = await supabase.rpc('accept_invitation_by_token', {
            token_input: inviteToken.value
        })

        if (error) throw error

        showToast("Invitation Accepted! You have joined the team.", "success")

        // Context Switch
        if (data && data.shop_id) {
            coreState.currentShopId = data.shop_id
            localStorage.setItem('lewotobi_shop_id', data.shop_id)

            // Refresh Profile & Context
            await auth.fetchUserProfile(auth.authState.user.id)
        }

        clearToken()
        showConfirmation.value = false

        // Go Home if not there
        if (route.name !== 'home') {
            router.push({ name: 'home' })
        }

    } catch (e) {
        console.error("Failed to accept invite:", e)
        // Extract clean message
        let msg = e.message;
        if (msg.includes('Application error:')) {
            msg = msg.replace('Application error:', '').trim();
        }

        showToast(msg, "error");

        // If expired/invalid/already member, clear token to prevent loop
        clearToken()
        showConfirmation.value = false
    }
}

const handleLogout = async () => {
    clearToken() // Assume they don't want this invite on this device if logging out? 
    // Or keep it? keeping it might re-trigger on next login.
    // User clicked "No, Log out", implying "Not this account".
    // BUT maybe they want to log in with another account to accept it.
    // So DO NOT clear token from storage, but clear local ref.

    showConfirmation.value = false
    await auth.logout(true)
    router.push({ name: 'start' })
}

const handleClose = () => {
    showConfirmation.value = false
    clearToken()
}

const clearToken = () => {
    inviteToken.value = null
    sessionStorage.removeItem('invite_token')
    localStorage.removeItem('invite_token')
    // Remove from URL if present
    const url = new URL(window.location)
    if (url.searchParams.has('invite_token')) {
        url.searchParams.delete('invite_token')
        window.history.replaceState({}, '', url)
    }
}
</script>

<template>
    <div v-if="showConfirmation"
        class="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
        <div
            class="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-md w-full p-8 text-center space-y-6 animate-in zoom-in-95 duration-200 border border-gray-100 dark:border-gray-800 relative">

            <button @click="handleClose"
                class="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                </svg>
            </button>

            <div
                class="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <Store :size="32" />
            </div>

            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Accept Invitation?</h2>

            <div class="bg-gray-50 dark:bg-slate-800 p-4 rounded-xl border border-gray-100 dark:border-slate-700">
                <p class="text-sm text-gray-500 dark:text-gray-400 mb-1">You are currently logged in as</p>
                <p class="font-bold text-gray-900 dark:text-gray-200 break-all">{{ auth.authState.user?.email }}</p>
            </div>

            <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                Do you want to accept this invitation and join the team with this account?
            </p>

            <div class="space-y-3 pt-2">
                <button @click="handleConfirm"
                    class="w-full py-3.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20">
                    Yes, Join Team
                </button>
                <button @click="handleLogout"
                    class="w-full py-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium text-sm">
                    No, Log out
                </button>
            </div>
        </div>
    </div>
</template>
